import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { Box, Stack, Tab, Typography, useTheme } from '@mui/material';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
    useGetTextReplacementQuery,
    usePostTextReplacementsMutation,
} from '@/api/v2/textReplacement';
import { useAuthentication } from '@/hooks/useAuthentication';
import {
    ProductTextReplacement,
    TextReplacementBlock,
    TextReplacementIndex,
    TextReplacementIndexKey,
} from '@/models/textReplacement';
import cloneDeep from 'lodash/cloneDeep';
import debounce from 'lodash/debounce';
import { useNotification } from '@starjack/features/notifications';
import { useSlice } from '@/hooks/useSlice';
import { pageDataSlice } from '@starjack/features/pageData';
import { TextReplacementsCard } from '@/pages/textCustomizations/components/TextReplacementCard';
import { ProductCombinationCard } from '@/pages/textCustomizations/components/ProductCombinationCard';

type TabValue = TextReplacementIndexKey | 'product';

export const MUTATION_CACHE_KEY = 'TEXT_REPLACEMENT';

const filterFn = (text: string, searchText: string): boolean => {
    if (!text || !searchText) return false;

    const normalizedText = text.trim().toLocaleLowerCase();
    const normalizedSearchText = searchText.trim().toLocaleLowerCase();
    const split = normalizedText.split(' ');
    const searchSplit = normalizedSearchText.split(' ');

    return searchSplit.every((searchSplit) => {
        return split.some((split) => split.includes(searchSplit));
    });
};

const searchComparison = (
    textBlocks: TextReplacementBlock[],
    searchTerm = ''
) => {
    return textBlocks.filter(
        (textBlock) =>
            filterFn(textBlock.oldText, searchTerm) ||
            filterFn(textBlock.newText, searchTerm)
    );
};

export const TextReplacementContent: FunctionComponent = () => {
    const {
        liftOperatorData: { liftOperatorId },
    } = useAuthentication();
    const theme = useTheme();
    const [tabValue, setTabValue] = useState<TabValue>(
        TextReplacementIndexKey.PERSON_NAME
    );
    const { data } = useGetTextReplacementQuery({ liftOperatorId });
    const [postTextReplacements] = usePostTextReplacementsMutation({
        fixedCacheKey: MUTATION_CACHE_KEY,
    });
    const [textReplacementIndex, setTextReplacementIndex] =
        useState<TextReplacementIndex>(null);
    const [productTextReplacements, setProductTextReplacements] = useState<
        ProductTextReplacement[]
    >(data?.productTextReplacements);
    const { addNotification } = useNotification();
    const {
        actions: { setPageData },
    } = useSlice(pageDataSlice, 'requestSlice');

    const handleTabChange = (_, newValue: TabValue) => {
        // reset search
        setTextReplacementIndex(data?.textReplacementIndex);

        setTabValue(newValue);
    };

    const onSearch = useMemo(
        () => (replacementKeyIndex: TextReplacementIndexKey) =>
            debounce((searchTerm) => {
                if (searchTerm) {
                    setTextReplacementIndex(() => ({
                        [replacementKeyIndex]: searchComparison(
                            data?.textReplacementIndex[replacementKeyIndex] ||
                                [],
                            searchTerm
                        ),
                    }));
                } else {
                    setTextReplacementIndex(data?.textReplacementIndex);
                }
            }, 350),
        [data]
    );

    const handleProductNameUpdate = (
        productTextReplacements: ProductTextReplacement[]
    ) => {
        setProductTextReplacements(productTextReplacements);
    };

    const handleChange =
        (textReplacementIndexKey: TextReplacementIndexKey) =>
        (newValue: string, index: number) => {
            const cloned = cloneDeep(textReplacementIndex);
            cloned[textReplacementIndexKey][index] = {
                ...cloned[textReplacementIndexKey][index],
                newText: newValue,
            };

            setTextReplacementIndex(cloned);
        };

    const onSave = () => {
        const cleanedTextReplacements = Object.entries(
            textReplacementIndex
        ).reduce((textReplacements, [type, textBlocks]) => {
            return [
                ...textReplacements,
                ...textBlocks.map((block) => ({ type, ...block })),
            ];
        }, []);

        postTextReplacements({
            liftOperatorId: liftOperatorId,
            textReplacements: cleanedTextReplacements,
            productTextReplacements: productTextReplacements,
        })
            .unwrap()
            .then(() => {
                addNotification({
                    type: 'success',
                    message: 'Änderung erfolgreich gespeichert',
                });
            })
            .catch(() => {
                addNotification({
                    type: 'error',
                    message:
                        'Fehler beim Speichern. Bitte später noch einmal versuchen',
                });
            });
    };

    useEffect(() => {
        setPageData({
            title: 'Textanpassungen Tickets',
            body: (
                <>
                    <Typography gutterBottom>
                        Hier stehen die Personen- und Ticketbezeichnungen, wie
                        sie aktuell über das Zutrittssystem an starjack
                        übermittelt werden. Sie lassen sich hier für den
                        Onlineverkauf überschreiben. Bitte deutsche und
                        englische Sprachversion beachten! Mit der
                        „Produktkombination“ lassen sich einzelne
                        Personen-Ticket-Kombinationen noch detaillierter
                        textlich anpassen und auch die englische Version
                        ergänzen.
                    </Typography>

                    <Typography>
                        <Typography
                            sx={{
                                fontWeight: 500,
                            }}
                            component='span'
                        >
                            Tipp:{' '}
                        </Typography>
                        Für ausführliche Produktbeschreibungen das Feld
                        „Ticketinfo“ im Menü „Produktkonfiguration“ nutzen.
                    </Typography>
                </>
            ),
        });
    }, []);

    useEffect(() => {
        if (data) {
            setTextReplacementIndex(data.textReplacementIndex);
            setProductTextReplacements(data.productTextReplacements);
        }
    }, [data]);

    return (
        <TabContext value={tabValue}>
            <Typography
                sx={{
                    mb: 8,
                }}
                variant='h4'
            >
                Textanpassungen Tickets
            </Typography>

            <Stack spacing={theme.page.verticalSpacing}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <TabList onChange={handleTabChange}>
                        <Tab
                            label='Personenbezeichnung'
                            value={TextReplacementIndexKey.PERSON_NAME}
                        />
                        <Tab
                            label='Ticketbezeichnung'
                            value={TextReplacementIndexKey.TICKET_NAME}
                        />
                        <Tab label='Produktkombination' value='product' />
                    </TabList>
                </Box>

                {!!textReplacementIndex && (
                    <>
                        <TabPanel value={TextReplacementIndexKey.PERSON_NAME}>
                            <TextReplacementsCard
                                title='Personenbezeichnung anpassen'
                                onInputBlur={handleChange(
                                    TextReplacementIndexKey.PERSON_NAME
                                )}
                                onSearch={onSearch(
                                    TextReplacementIndexKey.PERSON_NAME
                                )}
                                textReplacementBlocks={
                                    textReplacementIndex[
                                        TextReplacementIndexKey.PERSON_NAME
                                    ] || []
                                }
                                onSave={onSave}
                            />
                        </TabPanel>

                        <TabPanel value={TextReplacementIndexKey.TICKET_NAME}>
                            <TextReplacementsCard
                                title='Ticketbezeichnung anpassen'
                                onInputBlur={handleChange(
                                    TextReplacementIndexKey.TICKET_NAME
                                )}
                                onSearch={onSearch(
                                    TextReplacementIndexKey.TICKET_NAME
                                )}
                                textReplacementBlocks={
                                    textReplacementIndex[
                                        TextReplacementIndexKey.TICKET_NAME
                                    ]
                                }
                                onSave={onSave}
                            />
                        </TabPanel>
                    </>
                )}

                <TabPanel value='product'>
                    <ProductCombinationCard
                        onChangeProductPersonNames={handleProductNameUpdate}
                        onSave={onSave}
                    />
                </TabPanel>
            </Stack>
        </TabContext>
    );
};
